import { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TextInput,
  useListContext,
  type RaRecord,
} from "react-admin";
import {
  formatRosterBreakdownParts,
  parseRosterThaliTypeCounts,
  rosterEmptyMessage,
} from "./roster-breakdown";

type FmbDistributionDashboardMeta = {
  hasDailyMenu?: boolean;
  dashboardDate?: string;
};

type DashboardRow = RaRecord & {
  name?: string;
  total?: number;
  thaliTypeCounts?: Array<{ name?: string; count?: number }>;
};

const rosterFilters = [
  <TextInput
    key="q"
    source="q"
    label="Search distributor or roster"
    alwaysOn
    resettable
    sx={{ minWidth: 260 }}
  />,
];

function RosterBody({
  date,
  onHasDailyMenu,
}: {
  date: string;
  onHasDailyMenu?: (hasDailyMenu: boolean) => void;
}) {
  const { meta, isLoading, data, filterValues } = useListContext<DashboardRow>();
  const dashboardMeta = meta as FmbDistributionDashboardMeta | undefined;
  const hasDailyMenu = dashboardMeta?.hasDailyMenu !== false;
  const awaitingDate = dashboardMeta?.dashboardDate !== date;
  const pending = (isLoading ?? false) || awaitingDate;
  const hasSearch = Boolean(String((filterValues as { q?: unknown })?.q ?? "").trim());
  const rowCount = pending ? 0 : (data?.length ?? 0);
  const emptyMessage = rosterEmptyMessage({
    loading: pending,
    hasDailyMenu,
    rowCount,
    hasSearch,
  });

  useEffect(() => {
    if (pending || !onHasDailyMenu) {
      return;
    }
    onHasDailyMenu(hasDailyMenu);
  }, [hasDailyMenu, pending, onHasDailyMenu]);

  if (emptyMessage) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Datagrid bulkActionButtons={false}>
      <TextField source="name" label="Distributor" />
      <FunctionField<DashboardRow>
        label="Roster (thalis)"
        sortable={false}
        render={(record) => {
          const total = typeof record.total === "number" ? record.total : 0;
          const parts = formatRosterBreakdownParts(
            parseRosterThaliTypeCounts(record.thaliTypeCounts)
          );
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
              {parts.length > 0 ? (
                <Typography variant="body2" color="text.secondary" component="span">
                  {parts.join(" · ")}
                </Typography>
              ) : null}
              <Typography variant="body2" component="span" fontWeight={600}>
                Total: {total}
              </Typography>
            </Box>
          );
        }}
      />
    </Datagrid>
  );
}

type DailyRosterSectionProps = {
  date: string;
  onHasDailyMenu?: (hasDailyMenu: boolean) => void;
};

export default function DailyRosterSection({ date, onHasDailyMenu }: DailyRosterSectionProps) {
  const filter = useMemo(() => ({ date }), [date]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Daily thali roster
      </Typography>
      <List
        key={date}
        resource="fmbThaliDistributionDailyRun"
        filter={filter}
        disableSyncWithLocation
        title={false}
        perPage={50}
        filters={rosterFilters}
        actions={false}
        sort={{ field: "name", order: "ASC" }}
        exporter={false}
        pagination={false}
        empty={false}
      >
        <RosterBody date={date} onHasDailyMenu={onHasDailyMenu} />
      </List>
    </Box>
  );
}
